
import React, { useMemo, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import TicketFeature from './TicketFeature';
import { TicketType } from './ticketTypes';
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Star, Award, BadgeCheck, Barcode } from "lucide-react";

interface TicketCardProps {
  ticket: TicketType;
  index: number;
  timestamp: string;
  forceRender: number;
  updateCount: number;
}

const TicketCard = ({ ticket, index, forceRender, updateCount }: TicketCardProps) => {
  // Create stable ticket key based on ticket ID and force render count
  const ticketKey = useMemo(() => `ticket-${ticket.id}-${forceRender}`, 
    [ticket.id, forceRender]);
  
  const [isHovered, setIsHovered] = useState(false);
  
  // Get animation style based on ticket ID
  const getAnimationStyle = (ticketId: number) => {
    switch (ticketId) {
      case 1: // Early Bird
        return "hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-500";
      case 2: // VIP Pass
        return "animate-pulse-glow transition-all duration-500";
      case 3: // Full Price
        return "hover:shadow-xl hover:shadow-yellow-500/30 transition-all duration-500";
      default:
        return "";
    }
  };
  
  // Get icon based on ticket ID
  const getTicketIcon = (ticketId: number) => {
    switch (ticketId) {
      case 1: // Early Bird
        return <Star className="h-5 w-5 text-yellow-400" />;
      case 2: // VIP Pass
        return <Award className="h-5 w-5 text-red-400" />;
      case 3: // Full Price
        return <BadgeCheck className="h-5 w-5 text-blue-400" />;
      default:
        return null;
    }
  };
  
  // Get background style based on ticket ID - All now use red backgrounds
  const getBackgroundStyle = (ticketId: number, isPopular: boolean) => {
    return "bg-gradient-to-b from-red-900 to-black";
  };
  
  // Get border style based on ticket ID
  const getBorderStyle = (ticketId: number, isPopular: boolean) => {
    switch (ticketId) {
      case 1: // Early Bird
        return isHovered ? "border-red-400 border-2" : "border-red-500 border-2";
      case 2: // VIP Pass
        return isHovered ? "border-red-400 border-2" : "border-red-500 border-2";
      case 3: // Full Price
        return isHovered ? "border-red-400 border-2" : "border-red-500 border-2";
      default:
        return "border-red-500 border-2";
    }
  };

  // Get header box gradient based on ticket ID - All now use red gradients
  const getHeaderGradient = (ticketId: number) => {
    return "bg-gradient-to-br from-red-500/40 to-white/5";
  };
  
  // Get text color for barcode based on ticket ID
  const getBarcodeTextColor = (ticketId: number) => {
    return "text-red-300";
  };

  // Generate a random barcode ID
  const barcodeId = useMemo(() => Math.floor(10000000 + Math.random() * 90000000).toString(), []);

  return (
    <Card 
      key={ticketKey} 
      className={cn(
        `${getBackgroundStyle(ticket.id, ticket.popular)} backdrop-blur-sm overflow-hidden relative`,
        `${getBorderStyle(ticket.id, ticket.popular)}`,
        `${getAnimationStyle(ticket.id)}`,
        ticket.popular ? "transform scale-105 shadow-lg shadow-red-500/20" : "",
        "group"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {ticket.popular && (
        <div className="absolute top-0 right-0">
          <div className="bg-red-600 text-white text-xs px-4 py-1 rounded-bl-lg font-medium animate-pulse">
            Most Popular
          </div>
        </div>
      )}

      {/* Header box with ticket info and vertical barcode - increased height for consistency */}
      <div className={cn(
        "p-6 pb-8 rounded-t-lg mb-0 relative overflow-hidden min-h-[200px] flex flex-col justify-center",
        getHeaderGradient(ticket.id),
        "border-b border-red-500/30"
      )}>
        {/* Vertical barcode on right side */}
        <div className="absolute right-4 top-0 bottom-0 w-8 flex items-center justify-center">
          <div className="h-full w-10 flex flex-col justify-center">
            {Array.from({ length: 30 }).map((_, i) => (
              <div 
                key={`barcode-${i}`} 
                className={`h-[2px] w-full bg-black ${i % 3 === 0 ? 'h-[4px]' : i % 5 === 0 ? 'h-[3px]' : 'h-[2px]'}`}
                style={{ marginBottom: '2px' }}
              ></div>
            ))}
          </div>
        </div>
        
        {/* Content container with relative positioning for z-index */}
        <div className="relative z-10 text-center pr-12 flex flex-col justify-center h-full">
          {/* Price moved to the top and made bigger */}
          <div className="mb-4 transform transition-all duration-300 group-hover:scale-110">
            <span className="text-white text-[3.3rem] font-bold">
              {ticket.price}
            </span>
          </div>

          {/* Type text moved below price */}
          <div className="flex items-center justify-center gap-2 mb-3">
            {getTicketIcon(ticket.id)}
            <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-300 to-white">
              {ticket.name}
            </span>
          </div>
          
          <p className="mt-2 text-sm text-red-200 leading-relaxed">
            {ticket.description}
          </p>
        </div>
      </div>
      
      <CardContent className="pt-4 px-6 bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
        {/* Paper texture overlay */}
        <div 
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `
              radial-gradient(circle at 1px 1px, rgba(0,0,0,0.05) 1px, transparent 0),
              radial-gradient(circle at 3px 7px, rgba(0,0,0,0.03) 1px, transparent 0),
              radial-gradient(circle at 7px 2px, rgba(0,0,0,0.04) 1px, transparent 0),
              radial-gradient(circle at 12px 9px, rgba(0,0,0,0.02) 1px, transparent 0),
              radial-gradient(circle at 15px 4px, rgba(0,0,0,0.06) 1px, transparent 0)
            `,
            backgroundSize: '20px 20px, 25px 25px, 18px 18px, 30px 30px, 22px 22px',
            backgroundPosition: '0 0, 5px 5px, 10px 10px, 15px 15px, 20px 20px'
          }}
        ></div>
        
        {/* Additional subtle paper fibers */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(45deg, transparent 48%, rgba(0,0,0,0.02) 49%, rgba(0,0,0,0.02) 51%, transparent 52%),
              linear-gradient(-45deg, transparent 48%, rgba(0,0,0,0.02) 49%, rgba(0,0,0,0.02) 51%, transparent 52%)
            `,
            backgroundSize: '3px 3px, 4px 4px'
          }}
        ></div>
        
        {/* Content with relative positioning to stay above texture */}
        <div className="relative z-10">
          <ul className="space-y-0">
            {ticket.features.map((feature, featureIndex) => (
              <TicketFeature 
                key={`feature-${featureIndex}-${ticket.id}`}
                feature={feature} 
                ticketKey={ticketKey} 
                featureIndex={featureIndex}
                isLast={featureIndex === ticket.features.length - 1}
                ticketId={ticket.id}
              />
            ))}
          </ul>
        </div>
      </CardContent>
      <CardFooter className="bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
        {/* Paper texture for footer */}
        <div 
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `
              radial-gradient(circle at 1px 1px, rgba(0,0,0,0.05) 1px, transparent 0),
              radial-gradient(circle at 3px 7px, rgba(0,0,0,0.03) 1px, transparent 0),
              radial-gradient(circle at 7px 2px, rgba(0,0,0,0.04) 1px, transparent 0)
            `,
            backgroundSize: '20px 20px, 25px 25px, 18px 18px'
          }}
        ></div>
        
        <Button 
          onClick={() => {
            window.open(ticket.shopifyLink, '_blank');
          }}
          className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-6 h-14 transition-all duration-300 hover:shadow-lg border-2 border-yellow-600 shadow-md relative z-10 text-lg"
        >
          {ticket.buttonText}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default TicketCard;
